import { useRef, useEffect } from "react";

interface AutoVideoProps {
  src: string;
  className?: string;
  isCrystal?: boolean;
  preload?: "auto" | "metadata" | "none";
  poster?: string;
  onAspectRatioDetected?: (ratio: number) => void;
}

/**
 * iOS/iPadOS-compatible autoplay video component.
 * Uses IntersectionObserver to play/pause based on visibility.
 * Includes fallbacks for touchstart and first scroll events.
 */
export const AutoVideo = ({
  src,
  className = "",
  isCrystal = false,
  preload = "metadata",
  poster,
  onAspectRatioDetected,
}: AutoVideoProps) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const vid = videoRef.current;
    const container = containerRef.current;
    if (!vid || !container) return;

    // Force required attributes for iOS/iPadOS
    vid.muted = true;
    vid.playsInline = true;
    vid.setAttribute("muted", "");
    vid.setAttribute("playsinline", "");
    vid.setAttribute("webkit-playsinline", "true");
    vid.setAttribute("x-webkit-airplay", "deny");

    // iOS play fix: load then attempt play
    try {
      vid.load();
    } catch {
      // ignore
    }

    const playVideo = () => {
      if (vid.paused) {
        const promise = vid.play();
        if (promise !== undefined) {
          promise.catch(() => {
            // Autoplay blocked, will retry on interaction
          });
        }
      }
    };

    const pauseVideo = () => {
      if (!vid.paused) {
        vid.pause();
      }
    };

    // IntersectionObserver for visibility-based play/pause
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          playVideo();
        } else {
          pauseVideo();
        }
      },
      { threshold: 0.1 }
    );
    observer.observe(container);

    // Initial play attempt
    playVideo();

    // iOS fallback - play on first interaction
    const handleInteraction = () => {
      playVideo();
      cleanup();
    };

    // Scroll fallback
    const handleScroll = () => {
      playVideo();
    };

    const cleanup = () => {
      document.removeEventListener("touchstart", handleInteraction);
      document.removeEventListener("click", handleInteraction);
      window.removeEventListener("scroll", handleScroll);
    };

    document.addEventListener("touchstart", handleInteraction, { passive: true, once: true });
    document.addEventListener("click", handleInteraction, { passive: true, once: true });
    window.addEventListener("scroll", handleScroll, { passive: true, once: true });

    // Video loadeddata event for additional play attempt
    const handleLoaded = () => playVideo();
    vid.addEventListener("loadeddata", handleLoaded);
    vid.addEventListener("canplay", handleLoaded);

    // Detect aspect ratio on metadata load
    const handleMetaForAspect = () => {
      if (vid.videoWidth && vid.videoHeight && onAspectRatioDetected) {
        const ratio = vid.videoWidth / vid.videoHeight;
        if (isFinite(ratio) && ratio > 0) {
          onAspectRatioDetected(ratio);
        }
      }
    };
    vid.addEventListener("loadedmetadata", handleMetaForAspect);

    return () => {
      observer.disconnect();
      cleanup();
      vid.removeEventListener("loadeddata", handleLoaded);
      vid.removeEventListener("canplay", handleLoaded);
      vid.removeEventListener("loadedmetadata", handleMetaForAspect);
    };
  }, []);

  // Determine object-fit class
  const objectFit = isCrystal ? "object-contain" : "object-cover";
  const combinedClassName = `${objectFit} ${className}`.trim();

  return (
    <div ref={containerRef} className="absolute inset-0 w-full h-full overflow-hidden">
      <video
        ref={videoRef}
        src={src}
        autoPlay
        muted
        loop
        playsInline
        disablePictureInPicture
        preload={preload}
        poster={poster}
        className={`absolute inset-0 w-full h-full ${combinedClassName}`}
        style={{ background: "transparent", transform: "scale(1.08)" }}
      >
        <source src={src} type="video/mp4" />
      </video>
    </div>
  );
};

export default AutoVideo;
